/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { ReduxUtils } from '@spm/core';

import {
  GET_YES_NO_CODETABLE,
  GET_CODETABLES_BY_TABLE_NAME,
  GET_NOTIFICATION_TYPE_CODETABLE,
  GET_GENDER_CODETABLE,
  GET_STATE_CODETABLE,
} from './actionTypes';

import {
  getYesNoCodetableUtil,
  getCodetablesByTableNameUtil,
  getNotificationTypeCodetableUtil,
  getGenderCodetableUtil,
  getStateCodetableUtil,
} from './utils';

/**
 * Contains the action creator functions that update the SDCodeTableService slice of the store.
 *
 * @namespace SDCodeTableServiceActions
 */
export default class actions {
  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/codetables/{table_name}
   *
   * @param {string} table_name.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static getYesNoCodetable = (dispatch, table_name, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getYesNoCodetableUtil.bind(null, table_name),

      GET_YES_NO_CODETABLE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getYesNoCodetable.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetYesNoCodetable = (dispatch, data) => {
    const json = {
      type: GET_YES_NO_CODETABLE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getYesNoCodetable.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetYesNoCodetableError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_YES_NO_CODETABLE.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/codetables/{table_name}
   *
   * @param {string} table_name.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static getCodetablesByTableName = (dispatch, table_name, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getCodetablesByTableNameUtil.bind(null, table_name),

      GET_CODETABLES_BY_TABLE_NAME,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getCodetablesByTableName.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetCodetablesByTableName = (dispatch, data) => {
    const json = {
      type: GET_CODETABLES_BY_TABLE_NAME.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getCodetablesByTableName.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetCodetablesByTableNameError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_CODETABLES_BY_TABLE_NAME.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/codetables/{table_name}
   *
   * @param {string} table_name.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static getNotificationTypeCodetable = (dispatch, table_name, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getNotificationTypeCodetableUtil.bind(null, table_name),

      GET_NOTIFICATION_TYPE_CODETABLE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getNotificationTypeCodetable.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetNotificationTypeCodetable = (dispatch, data) => {
    const json = {
      type: GET_NOTIFICATION_TYPE_CODETABLE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getNotificationTypeCodetable.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetNotificationTypeCodetableError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_NOTIFICATION_TYPE_CODETABLE.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/codetables/{table_name}
   *
   * @param {string} table_name.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static getGenderCodetable = (dispatch, table_name, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getGenderCodetableUtil.bind(null, table_name),

      GET_GENDER_CODETABLE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getGenderCodetable.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetGenderCodetable = (dispatch, data) => {
    const json = {
      type: GET_GENDER_CODETABLE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getGenderCodetable.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetGenderCodetableError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_GENDER_CODETABLE.failureAction);
  };

  /**
   * Calls the api and dispatches the results to the store.
   * api: /v1/codetables/{table_name}
   *
   * @param {string} table_name.
   * @param {any} callback function to call once this function has finished
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static getStateCodetable = (dispatch, table_name, callback) => {
    ReduxUtils.generateAction(
      dispatch,
      getStateCodetableUtil.bind(null, table_name),

      GET_STATE_CODETABLE,
      callback
    );
  };

  /**
   * Dispatches the data to the store to reset the data cached from a call to getStateCodetable.
   *
   * @param {function} dispatch
   * @param {Object} data object to be set in the store.
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetStateCodetable = (dispatch, data) => {
    const json = {
      type: GET_STATE_CODETABLE.successAction,
      payload: data,
    };
    dispatch(json);
  };

  /**
   * Dispatches the data to the store to reset the error cached from a call to getStateCodetable.
   *
   * @param {function} dispatch
   * @function
   * @memberof SDCodeTableServiceActions
   */
  static resetGetStateCodetableError = dispatch => {
    ReduxUtils.resetErrorAction(dispatch, GET_STATE_CODETABLE.failureAction);
  };
}
